import { Eye, Package2, Tags, Grid3X3, Database, Upload, ExternalLink, Settings, AlertCircle, ChevronDown, ChevronUp, CheckCircle2, Info, ShoppingBag, LayoutGrid, Store, Sparkles } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { ProductCategories } from '../../components/products/ProductCategories';
import { ProductDisplaySettings } from '../../components/products/ProductDisplaySettings';
import { ProductImporter } from '../../components/products/ProductImporter';
import { ProductIntegrations } from '../../components/products/ProductIntegrations';
import { ProductList } from '../../components/products/ProductList';
import { ProductTags } from '../../components/products/ProductTags';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useMiniAppModal } from '../../hooks/useMiniAppModal';
import { useMiniApps } from '../../hooks/useMiniApps';
import { useProductSystem } from '../../hooks/useProductSystem';

export default function ProductsSettings() {
  const { chatbotPublicId } = useChatbot();
  const { openModal } = useMiniAppModal();
  const { miniApps, updateMiniAppLabel, loading: miniAppsLoading, error: miniAppsError, initialized: miniAppsInitialized, initializeMiniApps } = useMiniApps();
  const { loading: productLoading, error: productError, initialized: productInitialized, initialize: initializeProducts } = useProductSystem();
  const [activeTab, setActiveTab] = useState<'general' | 'integrations'>('general');
  const [openGuide, setOpenGuide] = useState<'shopify' | 'woocommerce' | 'magento' | 'csv' | null>(null);

  // Initialize mini apps and product system when component mounts
  useEffect(() => {
    if (chatbotPublicId && !miniAppsInitialized) {
      initializeMiniApps(chatbotPublicId);
    }
  }, [chatbotPublicId, miniAppsInitialized, initializeMiniApps]);

  useEffect(() => {
    if (chatbotPublicId && !productInitialized) {
      initializeProducts(chatbotPublicId);
    }
  }, [chatbotPublicId, productInitialized, initializeProducts]);

  const productsApp = miniApps.find(app => app.id === 'products');
  
  const loading = (miniAppsLoading || productLoading) && (!miniAppsInitialized || !productInitialized);
  const error = miniAppsError || productError;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-12 h-12 rounded-full border-l-2 border-r-2 border-t-2 border-indigo-500 animate-spin" />
      </div>
    );
  }

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (chatbotPublicId) {
      updateMiniAppLabel('products', e.target.value, chatbotPublicId);
    }
  };

  return (
    <div className="space-y-8 max-w-full">
      {/* Header with title and preview button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-5">
          <div className="p-4 rounded-xl bg-gradient-to-br from-violet-500/20 to-blue-400/20 text-white shadow-lg">
            <Package2 className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Product Management</h2>
            <p className="text-gray-400 mt-1">
              Configure your products catalog and e-commerce integrations
            </p>
          </div>
        </div>
        <button
          onClick={() => openModal('products')}
          className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-violet-500 to-blue-400 hover:from-violet-600 hover:to-blue-500 text-white rounded-lg transition-all shadow-lg font-medium"
        >
          <Eye className="w-4 h-4" />
          Preview Mini-App
        </button>
      </div>

      {/* Tabs for navigation */}
      <div className="flex space-x-2 border-b border-white/10 mb-8">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'general'
              ? 'text-white border-b-2 border-indigo-400 bg-white/5'
              : 'text-gray-400 hover:text-gray-300 hover:bg-white/5'
          } rounded-t-lg`}
        >
          General Settings
        </button>
        <button
          onClick={() => setActiveTab('integrations')}
          className={`px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'integrations'
              ? 'text-white border-b-2 border-indigo-400 bg-white/5'
              : 'text-gray-400 hover:text-gray-300 hover:bg-white/5'
          } rounded-t-lg`}
        >
          E-commerce Integrations
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg border border-red-500/20 flex items-center gap-3 animate-fade-in">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error instanceof Error ? error.message : String(error)}</p>
        </div>
      )}

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="space-y-8">
          {/* Mini-app configuration */}
          <div className="bg-gradient-to-br from-gray-900 to-black/80 rounded-xl p-6 shadow-xl border border-white/5">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-indigo-600/20 text-indigo-400">
                <Settings className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-medium text-white">Mini-App Configuration</h3>
            </div>
            
            <div className="space-y-6">
              <div className="bg-black/30 p-5 rounded-lg border border-white/5">
                <label htmlFor="app_label" className="block text-sm font-medium text-gray-300 mb-2">
                  Mini-App Title
                </label>
                <input
                  id="app_label"
                  type="text"
                  value={productsApp?.label || ''}
                  onChange={handleLabelChange}
                  className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-all"
                  placeholder="Products"
                />
                <p className="mt-2 text-xs text-gray-400">
                  This title will be displayed to users in the chatbot interface
                </p>
              </div>
            </div>
          </div>

          {/* Display Settings */}
          <div className="bg-gradient-to-br from-gray-900 to-black/80 rounded-xl p-6 shadow-xl border border-white/5">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-indigo-600/20 text-indigo-400">
                <LayoutGrid className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-medium text-white">Display Settings</h3>
            </div>
            
            <div className="bg-black/30 p-5 rounded-lg border border-white/5">
              <ProductDisplaySettings />
            </div>
          </div>

          {/* Categories and Tags */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-gray-900 to-black/80 rounded-xl p-6 shadow-xl border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-indigo-600/20 text-indigo-400">
                  <Database className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-medium text-white">Categories</h3>
              </div>
              
              <div className="bg-black/30 p-5 rounded-lg border border-white/5">
                <ProductCategories />
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black/80 rounded-xl p-6 shadow-xl border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-indigo-600/20 text-indigo-400">
                  <Tags className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-medium text-white">Tags</h3>
              </div>
              
              <div className="bg-black/30 p-5 rounded-lg border border-white/5">
                <ProductTags />
              </div>
            </div>
          </div>

          {/* Product Import */}
          <div className="bg-gradient-to-br from-gray-900 to-black/80 rounded-xl p-6 shadow-xl border border-white/5">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-indigo-600/20 text-indigo-400">
                <Upload className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-medium text-white">Import Products</h3>
            </div>
            
            <div className="bg-black/30 p-5 rounded-lg border border-white/5">
              <ProductImporter />
            </div>
            
            {/* Setup Guide for CSV Import */}
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setOpenGuide(openGuide === 'csv' ? null : 'csv')}
                className="flex items-center justify-between w-full p-4 bg-indigo-900/20 border border-indigo-500/30 rounded-lg text-white hover:bg-indigo-900/30 transition-colors"
              >
                <div className="flex items-center">
                  <Info className="w-5 h-5 text-indigo-400 mr-2" />
                  <span className="font-medium">CSV Import Guide</span>
                </div>
                {openGuide === 'csv' ? (
                  <ChevronUp className="w-5 h-5 text-indigo-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-indigo-400" />
                )}
              </button>
              
              {openGuide === 'csv' && (
                <div className="mt-4 p-5 bg-black/40 rounded-lg border border-white/10 text-sm text-gray-300 space-y-4">
                  <h4 className="font-medium text-white">CSV Import Format Guide</h4>
                  <p>Follow these guidelines to prepare your CSV file for import:</p>
                  
                  <div className="bg-gray-800 p-4 rounded font-mono text-xs overflow-x-auto">
                    <p className="text-indigo-300 mb-2"># Required columns</p>
                    <p>name,description,price,image_url,category,sku</p>
                    <p className="text-indigo-300 mt-3 mb-2"># Optional columns</p>
                    <p>tags,in_stock,variants,options</p>
                  </div>
                  
                  <div className="space-y-3 mt-2">
                    <h5 className="font-medium text-white">Column Requirements:</h5>
                    <ul className="space-y-2 list-disc pl-5">
                      <li><span className="text-white">name</span>: Product name (required)</li>
                      <li><span className="text-white">description</span>: Product description (required)</li>
                      <li><span className="text-white">price</span>: Numeric price without currency symbol (required)</li>
                      <li><span className="text-white">image_url</span>: URL to the product image (required)</li>
                      <li><span className="text-white">category</span>: Product category (required)</li>
                      <li><span className="text-white">sku</span>: Unique product identifier (required)</li>
                      <li><span className="text-white">tags</span>: Comma-separated list of tags (optional)</li>
                      <li><span className="text-white">in_stock</span>: true or false (optional, defaults to true)</li>
                      <li><span className="text-white">variants</span>: JSON-formatted variants data (optional)</li>
                      <li><span className="text-white">options</span>: JSON-formatted options data (optional)</li>
                    </ul>
                  </div>
                  
                  <div className="bg-indigo-900/30 p-4 rounded-lg border border-indigo-500/30 flex items-start">
                    <Info className="w-5 h-5 text-indigo-400 mr-2 mt-0.5 flex-shrink-0" />
                    <p>For large product catalogs, we recommend using one of our e-commerce platform integrations instead of CSV import for better performance and automatic synchronization.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Product List */}
          <div className="bg-gradient-to-br from-gray-900 to-black/80 rounded-xl p-6 shadow-xl border border-white/5">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-indigo-600/20 text-indigo-400">
                <Package2 className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-medium text-white">Products</h3>
            </div>
            
            <div className="bg-black/30 p-5 rounded-lg border border-white/5">
              <ProductList />
            </div>
          </div>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-8">
          {/* E-commerce Integrations */}
          <div className="bg-gradient-to-br from-gray-900 to-black/80 rounded-xl p-6 shadow-xl border border-white/5">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-indigo-600/20 text-indigo-400">
                <ExternalLink className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-medium text-white">E-commerce Integrations</h3>
            </div>
            
            <div className="bg-black/30 p-5 rounded-lg border border-white/5 mb-6">
              <p className="text-gray-300 mb-6">
                Connect your online store to automatically import and synchronize your products with your chatbot
              </p>
              
              <ProductIntegrations />
            </div>
            
            {/* Platform Setup Guides */}
            <div className="space-y-4">
              {/* Shopify Guide */}
              <div>
                <button
                  type="button"
                  onClick={() => setOpenGuide(openGuide === 'shopify' ? null : 'shopify')}
                  className="flex items-center justify-between w-full p-4 bg-indigo-900/20 border border-indigo-500/30 rounded-lg text-white hover:bg-indigo-900/30 transition-colors"
                >
                  <div className="flex items-center">
                    <ShoppingBag className="w-5 h-5 text-indigo-400 mr-2" />
                    <span className="font-medium">Shopify Integration Guide</span>
                  </div>
                  {openGuide === 'shopify' ? (
                    <ChevronUp className="w-5 h-5 text-indigo-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-indigo-400" />
                  )}
                </button>
                
                {openGuide === 'shopify' && (
                  <div className="mt-4 p-5 bg-black/40 rounded-lg border border-white/10 text-sm text-gray-300 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ShoppingBag className="w-5 h-5 text-white" />
                      <h4 className="font-medium text-white text-lg">Shopify Integration Guide</h4>
                    </div>
                    
                    <p>Connect your Shopify store to automatically sync products and inventory:</p>
                    
                    <ol className="space-y-6 ml-5 list-decimal">
                      <li>
                        <div className="font-medium text-white">Create a private app in your Shopify admin</div>
                        <p className="text-gray-400 mt-1">Go to Apps → App and sales channel settings → Develop apps → Create an app</p>
                      </li>
                      <li>
                        <div className="font-medium text-white">Configure API permissions</div>
                        <p className="text-gray-400 mt-1">Add the following API access scopes:</p>
                        <ul className="list-disc ml-5 mt-2 space-y-1 text-gray-400">
                          <li>read_products</li>
                          <li>read_inventory</li>
                          <li>read_product_listings</li>
                        </ul>
                      </li>
                      <li>
                        <div className="font-medium text-white">Generate API credentials</div>
                        <p className="text-gray-400 mt-1">In the API credentials tab, install the app and note your Admin API access token</p>
                      </li>
                      <li>
                        <div className="font-medium text-white">Enter credentials in the integration form</div>
                        <p className="text-gray-400 mt-1">Enter your shop domain (yourstorename.myshopify.com) and the API token in our integration form</p>
                        <div className="bg-gray-800 p-3 rounded mt-2 flex items-center gap-3">
                          <CheckCircle2 className="text-green-500 w-5 h-5 flex-shrink-0" />
                          <p className="text-gray-300 text-xs">After connecting, your Shopify products will automatically sync with your chatbot's product catalog.</p>
                        </div>
                      </li>
                    </ol>
                    
                    <div className="bg-indigo-900/30 p-4 rounded-lg border border-indigo-500/30 flex items-start mt-4">
                      <Sparkles className="w-5 h-5 text-indigo-400 mr-2 mt-0.5 flex-shrink-0" />
                      <p>Pro tip: Our Shopify integration supports automatic inventory updates so your chatbot will always show accurate stock information.</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* WooCommerce Guide */}
              <div>
                <button
                  type="button"
                  onClick={() => setOpenGuide(openGuide === 'woocommerce' ? null : 'woocommerce')}
                  className="flex items-center justify-between w-full p-4 bg-indigo-900/20 border border-indigo-500/30 rounded-lg text-white hover:bg-indigo-900/30 transition-colors"
                >
                  <div className="flex items-center">
                    <Store className="w-5 h-5 text-indigo-400 mr-2" />
                    <span className="font-medium">WooCommerce Integration Guide</span>
                  </div>
                  {openGuide === 'woocommerce' ? (
                    <ChevronUp className="w-5 h-5 text-indigo-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-indigo-400" />
                  )}
                </button>
                
                {openGuide === 'woocommerce' && (
                  <div className="mt-4 p-5 bg-black/40 rounded-lg border border-white/10 text-sm text-gray-300 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Store className="w-5 h-5 text-white" />
                      <h4 className="font-medium text-white text-lg">WooCommerce Integration Guide</h4>
                    </div>
                    
                    <p>Connect your WooCommerce store to synchronize products with your chatbot:</p>
                    
                    <ol className="space-y-6 ml-5 list-decimal">
                      <li>
                        <div className="font-medium text-white">Generate API keys in WooCommerce</div>
                        <p className="text-gray-400 mt-1">In your WordPress admin, go to WooCommerce → Settings → Advanced → REST API → Add key</p>
                      </li>
                      <li>
                        <div className="font-medium text-white">Set permissions</div>
                        <p className="text-gray-400 mt-1">Give the key Read permissions and create it</p>
                      </li>
                      <li>
                        <div className="font-medium text-white">Copy credentials</div>
                        <p className="text-gray-400 mt-1">Note the Consumer Key and Consumer Secret provided</p>
                      </li>
                      <li>
                        <div className="font-medium text-white">Enter details in our integration form</div>
                        <p className="text-gray-400 mt-1">Enter your store URL, Consumer Key and Consumer Secret</p>
                        <div className="bg-gray-800 p-3 rounded mt-2 flex items-center gap-3">
                          <CheckCircle2 className="text-green-500 w-5 h-5 flex-shrink-0" />
                          <p className="text-gray-300 text-xs">We'll automatically import all your WooCommerce products including categories, tags, and variations.</p>
                        </div>
                      </li>
                    </ol>
                    
                    <div className="bg-indigo-900/30 p-4 rounded-lg border border-indigo-500/30 flex items-start mt-4">
                      <Info className="w-5 h-5 text-indigo-400 mr-2 mt-0.5 flex-shrink-0" />
                      <p>Make sure your WordPress site is publicly accessible and your REST API is enabled for the integration to work properly.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}